import React, { useCallback } from 'react';
import {
  CategoriesParam,
  ComboBoxMultiselectParam,
  Form,
  Row,
  SonForm,
  SubmitButton,
  useForm,
} from '../../../_lib/Form2';
import { ListBoxModifier } from '../../../components/molecules/Form/components/ComboBox';
import { PrivilegedGroupsSelectComboBox } from './PrivilegedGroupsSelectComboBox';
import { useDelayed } from '../../../hooks/useDelayed';
import { useModalContext } from '../../../components/organisms/ModalDialog/ModalContext';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { RowModifier } from '../../../_lib/Form2/grid/Row/Row';

export type PrivilegedGroupParam<TName extends string> = {
  active?: boolean;
} & CategoriesParam<TName>;

type PrivilegedGroupsAddFormProps = {
  form: SonForm<ComboBoxMultiselectParam<'groupId'> | PrivilegedGroupParam<'grantedRoles'>>;
  refreshView: () => void;
};

export function PrivilegedGroupsAddForm({ form, refreshView }: PrivilegedGroupsAddFormProps) {
  const { t } = useSonTranslation('PrivilegedGroups');

  const { closeModal } = useModalContext();
  const refreshAndClose = useCallback(() => {
    refreshView();
    closeModal();
  }, [closeModal, refreshView]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);
  const onSuccess = () => {
    delayedRefreshAndClose();
  };

  const { formProps, params, fetchState } = useForm({
    form,
    onSuccess,
  });

  const groupIdParam = params.getByName('groupId');
  const grantedRolesParam = params.getByName('grantedRoles');

  return (
    <Form id="privileged-groups-add-form" {...formProps}>
      {groupIdParam && grantedRolesParam && (
        <Row modifier={RowModifier.BLOCK}>
          <PrivilegedGroupsSelectComboBox
            suggestUrl={groupIdParam.comboBoxUrl.url}
            modifierListBox={ListBoxModifier.RELATIVE}
            labelTitle={t('selectGroup')}
            required={groupIdParam.required}
            grantedRolesParam={grantedRolesParam}
          />
        </Row>
      )}
      <SubmitButton
        buttonLabel={t('saveGroupsAndRoles')}
        messageSuccess={t('phrases.add.success')}
        messageSending={t('phrases.add.sending')}
        messageFail={t('phrases.add.fail')}
        fetchState={fetchState}
        customErrorTypeMessages={{
          missingButRequired: t('phrases.add.missingButRequired'),
        }}
      />
    </Form>
  );
}
